import { HeaderCopy } from 'components/city-blocks/header-copy';
import { MaxWidthContainer } from 'components/city-blocks/max-width-container';
import { ZipCodesListSection } from 'components/city-blocks/zip-codes-list-section';
import { HeroBlock } from 'components/hero-block/hero-block';
import Layout from 'components/layout';
import { generateBreadcrumbSchema, generateLocationListSchema, SchemaMarkup, SEOHead } from 'components/seo';
import { mapValues } from 'lodash';
import React from 'react';
import { NoFollowLink } from 'shared/no-follow-link';
import { fetchCitiesData } from 'utils/api-utils';
import { zipsRootBreadcrumbs } from 'utils/breadcrumb-util';
import { LINKS_BLOCK_LIMIT } from 'utils/constants';
import { getFrontEndUrl } from 'utils/environment-util';
import { RegionInfoFragment } from 'utils/gqlReactTypings.generated.d';
import { memoizedCityPageCities, memoizedRegionsById, memoizedZipPagesByRegionId } from 'utils/memoized-data-structure';
import { reduceZipPage, ZipPage } from 'utils/types';

interface IProps {
  zipPages: ZipPage[];
  zipsByRegion: { [identifier: string]: ZipPage[] };
  regionsById: { [identifier: string]: RegionInfoFragment };
}

const ZIP_HOME_META_DESC =
  'Shef delivers authentic homemade food to ZIP codes across the US. Find local chefs and authentic cuisines in your neighborhood!';
const HEADER_COPY_TITLE = 'Find a Local Delivery or Pickup Option Near You For Delicious Homemade Food!';

const invokeHeaderCopyContent = ({ numZips }: { numZips: number }): React.ReactElement => (
  <span>
    Shef delivers traditional homemade food to over {numZips} ZIP Codes throughout the US and has food pickup in many
    more! With thousands of local Shefs & dishes to choose from, you're sure to find the perfect homemade dish for
    breakfast, lunch, or dinner! Browse by metro, city, or{' '}
    <NoFollowLink
      href={getFrontEndUrl()}
      className='text-neutral-500 underline underline-offset-2 hover:text-neutral-700'>
      search by ZIP
    </NoFollowLink>
  </span>
);

const ZipCodesWeServe: React.FC<IProps> = ({ zipPages }: IProps) => {
  const title = 'See All US ZIP Codes Where Our Shefs Cook Homemade Food!';
  const heroText = 'ZIP Codes Where Shef Delivers';
  const siteUrl = getFrontEndUrl();
  const pageUrl = `${siteUrl}/zip-codes-we-serve`;

  const schemas = [
    generateBreadcrumbSchema([
      { name: 'Home', url: siteUrl },
      { name: 'ZIP Codes We Serve', url: pageUrl },
    ]),
    generateLocationListSchema(zipPages, 'zip', siteUrl),
  ];

  return (
    <Layout>
      <SEOHead
        title={title}
        description={ZIP_HOME_META_DESC}
        url={pageUrl}
        keywords={[
          'zip codes where shef delivers',
          'food delivery by zip code',
          'homemade food delivery zip codes',
          'local delivery zip codes',
          'neighborhood food delivery',
        ]}
        image={`${siteUrl}/images/CDN/food_bg.png`}
        canonical={pageUrl}
      />
      <SchemaMarkup schemas={schemas} id='zip-codes-schema' />
      <HeroBlock className='mb-10' heroText={heroText} crumbs={zipsRootBreadcrumbs} />
      <MaxWidthContainer className='px-4 md:px-0'>
        <HeaderCopy
          className='mb-12'
          title={HEADER_COPY_TITLE}
          content={invokeHeaderCopyContent({ numZips: zipPages.length })}
        />
      </MaxWidthContainer>

      <MaxWidthContainer className='px-4 md:px-0'>
        <ZipCodesListSection title='All ZIP codes Shef serves' zipPages={zipPages} />
      </MaxWidthContainer>
    </Layout>
  );
};

export async function getStaticProps() {
  const apiData = await fetchCitiesData();

  if (apiData?.cityPages && apiData?.citiesByRegion && apiData?.regionsById) {
    const zipPages = apiData.cityPages
      .map((cityPage) => ({
        __typename: 'ZipPage' as const,
        id: cityPage.id,
        cityName: cityPage.cityName,
        regionId: cityPage.regionId || 0,
        deliveryHubId: cityPage.deliveryHubId,
        stateNameShort: cityPage.stateNameShort,
        zipCode: cityPage.zipCode,
      }))
      .sort((a, b) => a.zipCode.localeCompare(b.zipCode));

    return {
      props: {
        zipPages,
        zipsByRegion: mapValues(apiData.citiesByRegion, (array) =>
          array.slice(0, LINKS_BLOCK_LIMIT).map((city) => ({
            __typename: 'ZipPage' as const,
            id: city.id,
            cityName: city.cityName,
            regionId: city.regionId || 0,
            deliveryHubId: city.deliveryHubId,
            stateNameShort: city.stateNameShort,
            zipCode: city.zipCode,
          }))
        ),
        regionsById: apiData.regionsById,
      },
    };
  }
  const cityPageCities = await memoizedCityPageCities();
  const zipPagesByRegionId = await memoizedZipPagesByRegionId();
  const regionsById = await memoizedRegionsById();

  return {
    props: {
      zipPages: cityPageCities
        .map((cityPage) => reduceZipPage(cityPage, cityPage.zipCode))
        .sort((a, b) => a.zipCode.localeCompare(b.zipCode)),
      zipsByRegion: mapValues(zipPagesByRegionId, (array) => array.slice(0, LINKS_BLOCK_LIMIT)),
      regionsById,
    },
  };
}

export default ZipCodesWeServe;
